import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';

import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { distinctUntilChanged } from 'rxjs';

import { selectMemberProfile } from '@hm/core/stores/member-hub/member-hub.selectors';

import { DEFAULT_PAGE_SIZE } from '@lib/commons/constants/request.const';

import { MemberProfile } from '@lib/core/models/member.interface';

import { DocumentDetailsFormComponent } from '@lib/medical-documents/components/document-details-form/document-details-form.component';
import { DocumentDetailsComponent } from '@lib/medical-documents/components/document-details/document-details.component';
import { AiDocumentsListComponent } from '@lib/medical-documents/components/documents-list/ai-documents-list/ai-documents-list.component';
import { PageConfig } from '@lib/medical-documents/core/models/medical-documents-state.interface';
import { APPOINTMENTS_SIGNAL } from '@lib/medical-documents/core/tokens/appointments.token';
import { MD_LIB_CONFIG } from '@lib/medical-documents/core/tokens/medical-documents.tokens';

import { SpinLoadingComponent } from '@lib/shared-ui/commons/spin-loading/spin-loading.component';
import { InfiniteScrollDirective } from '@lib/shared-ui/directives/infinite-scroll.directive';
import { LoaderComponent } from '@lib/shared-ui/layout/loader/loader.component';

import { DocumentSearchComponent } from './components/document-search/document-search.component';
import { DocumentPreviewComponent } from './components/document-viewer/document-preview/document-preview.component';
import { DocumentsListComponent } from './components/documents-list/regular-documents-list/documents-list.component';
import { AiMedicalDocumentItem, MedicalDocumentItem } from './core/models/document.interface';
import { DocumentDetailsState } from './core/models/medical-documents-state.interface';
import * as MedicalDocumentsActions from './core/store/medical-documents.actions';
import { setAiMedicalDataEnabled, setAiSearchFeatureEnabled } from './core/store/medical-documents.actions';
import {
  selectAiDocumentsList,
  selectAiSearchResponseMessage,
  selectCurrentAiDocument,
  selectDocumentDetailsState,
  selectDocumentsList,
  selectDocumentsPageConfig,
  selectDocumentsSearchTerm,
  selectFilteredAiDocuments,
  selectIsAiMedicalDataEnabled,
  selectIsAiSearchChecked,
  selectIsAiSearchFeatureEnabled,
  selectIsAiSearchLoading,
  selectIsDocumentsListLoading,
  selectUploadingDocuments,
} from './core/store/medical-documents.selectors';

@Component({
  selector: 'md-medical-documents',
  standalone: true,
  imports: [
    DocumentSearchComponent,
    DocumentsListComponent,
    DocumentPreviewComponent,
    LoaderComponent,
    InfiniteScrollDirective,
    SpinLoadingComponent,
    TranslatePipe,
    ReactiveFormsModule,
    AiDocumentsListComponent,
    DocumentDetailsFormComponent,
    DocumentDetailsComponent,
  ],
  templateUrl: './medical-documents.component.html',
  styles: `
    :host {
      @apply h-full;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MedicalDocumentsComponent implements OnInit {
  private readonly mdConfig = inject(MD_LIB_CONFIG);
  private readonly store = inject(Store);
  private readonly appointmentsSignal = inject(APPOINTMENTS_SIGNAL, { optional: true });
  private readonly destroyRef = inject(DestroyRef);

  protected readonly userAppointments = computed(() => (this.mdConfig.withAppointments && this.appointmentsSignal ? this.appointmentsSignal() : []));

  // Store selectors
  protected readonly memberProfile = computed(() => this.mdConfig.memberProfile || this.store.selectSignal<MemberProfile>(selectMemberProfile)());
  protected readonly isAiSearchFeatureEnabled = this.store.selectSignal<boolean>(selectIsAiSearchFeatureEnabled);
  protected readonly isAiMedicalDataEnabled = this.store.selectSignal<boolean>(selectIsAiMedicalDataEnabled);
  protected readonly isAiSearchChecked = this.store.selectSignal<boolean>(selectIsAiSearchChecked);
  protected readonly aiSearchResponseMessage = this.store.selectSignal<string>(selectAiSearchResponseMessage);
  protected readonly isAiSearchLoading = this.store.selectSignal<boolean>(selectIsAiSearchLoading);
  protected readonly documentsSearchTerm = this.store.selectSignal<string>(selectDocumentsSearchTerm);
  protected readonly documentsPageConfig = this.store.selectSignal<PageConfig>(selectDocumentsPageConfig);
  protected readonly documents = this.store.selectSignal<MedicalDocumentItem[]>(selectDocumentsList);
  protected readonly aiDocuments = this.store.selectSignal<AiMedicalDocumentItem[]>(selectAiDocumentsList);
  protected readonly filteredAiDocuments = this.store.selectSignal<AiMedicalDocumentItem[]>(selectFilteredAiDocuments);
  protected readonly loadingDocumentsList = this.store.selectSignal<boolean>(selectIsDocumentsListLoading);
  protected readonly currentAiDocument = this.store.selectSignal(selectCurrentAiDocument);
  protected readonly uploadingDocuments = this.store.selectSignal(selectUploadingDocuments);
  protected readonly detailsState = this.store.selectSignal(selectDocumentDetailsState);

  protected readonly displayDocuments = computed(() => (this.isAiSearchChecked() ? this.aiDocuments() : this.filteredAiDocuments()));

  // Feature flags computed from config
  protected readonly canEditDocuments = computed(() => this.mdConfig.documentsEditEnabled ?? true);
  protected readonly canUploadDocuments = computed(() => this.mdConfig.documentsUploadEnabled ?? true);
  protected readonly showAiContextSearch = computed(() => this.mdConfig.aiContextSearchEnabled ?? false);

  ngOnInit() {
    this.initFeatureFlags();
    if (this.mdConfig.withFirebase) this.initFirebaseEvents();
    this.initSearchWatcher();

    if (this.isAiMedicalDataEnabled()) this.getAiDocumentsByUserId(this.mdConfig.userId);
    else this.fetchRegularDocuments();
  }

  protected getAiDocumentsByUserId(userId: string) {
    this.store.dispatch(MedicalDocumentsActions.getAiDocumentsList({ userId }));
  }

  private initFeatureFlags() {
    const aiMedicalDataEnabled = this.mdConfig.aiMedicalDataEnabled || false;
    const aiContextSearchEnabled = this.mdConfig.aiContextSearchEnabled || false;

    this.store.dispatch(setAiMedicalDataEnabled({ enabled: aiMedicalDataEnabled }));
    this.store.dispatch(setAiSearchFeatureEnabled({ enabled: aiContextSearchEnabled }));
  }

  protected fetchRegularDocuments() {
    const memberId = this.mdConfig.memberId || this.memberProfile()?.id;
    if (memberId) return this.getDocumentsList(memberId, 1, DEFAULT_PAGE_SIZE, this.documentsSearchTerm());
  }

  protected fetchNextPage() {
    if (this.documentsPageConfig()?.hasNextPage) {
      const memberId = this.mdConfig.memberId || this.memberProfile()?.id;
      if (memberId) this.getDocumentsList(memberId, this.documentsPageConfig().page + 1, this.documentsPageConfig().pageSize, this.documentsSearchTerm());
    }
  }

  private getDocumentsList(memberId: string, page: number, pageSize: number, searchTerm: string) {
    this.store.dispatch(
      MedicalDocumentsActions.getDocumentsList({
        memberId,
        page,
        pageSize,
        searchTerm,
      })
    );
  }

  protected closeDetails() {
    this.store.dispatch(MedicalDocumentsActions.closeDocumentDetails());
  }

  protected openEditMode() {
    this.store.dispatch(MedicalDocumentsActions.editDocumentDetails());
  }

  protected onFormSubmitted(updatedDocument: AiMedicalDocumentItem) {
    if (updatedDocument) {
      const uploadingDocuments = this.uploadingDocuments();
      const isUploadingDocument = uploadingDocuments.some(doc => doc.uploadResponse?.id === updatedDocument.id);

      if (isUploadingDocument) this.store.dispatch(MedicalDocumentsActions.promoteUploadingToAiDocument({ updatedDocument }));
      else this.store.dispatch(MedicalDocumentsActions.updateAiDocument({ updatedDocument }));

      this.store.dispatch(MedicalDocumentsActions.setCurrentAiDocumentDetails({ currentAiDocument: updatedDocument }));
      this.store.dispatch(MedicalDocumentsActions.setDocumentDetailsState({ state: DocumentDetailsState.Opened }));
    }
  }

  protected onFormCancelled($event: boolean) {
    if ($event) this.store.dispatch(MedicalDocumentsActions.setDocumentDetailsState({ state: DocumentDetailsState.Opened }));
  }

  protected initFirebaseEvents() {
    this.store.dispatch(MedicalDocumentsActions.initFirebaseEventListener());
  }

  private initSearchWatcher() {
    this.store
      .select(selectDocumentsSearchTerm)
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.isAiMedicalDataEnabled()) {
          this.store.dispatch(MedicalDocumentsActions.clearDocumentsList());
          this.fetchRegularDocuments();
          return;
        }

        const isContextSearchActive = this.isAiSearchFeatureEnabled() && this.isAiSearchChecked();
        const hasSearchTerm = this.documentsSearchTerm().length > 0;

        if (isContextSearchActive) {
          this.store.dispatch(MedicalDocumentsActions.setAiSearchResponseMessage({ message: '' }));
          if (!hasSearchTerm) this.getAiDocumentsByUserId(this.mdConfig.userId);
        } else this.getAiDocumentsByUserId(this.mdConfig.userId);
      });
  }
}
